// Paying online through the payment provider's hosted page. The portal sends the browser to the page's address and,
// before it goes, remembers in this tab what the payment was for, so the page the provider sends the player back to
// (`/payment-complete`) can say what happened. Nothing is credited or issued until the provider confirms; the portal
// only asks the api how the payment stands.

import type { MyTopUpOffer, NonNegativeMoney } from '@clavian/api-client';

const STORAGE_KEY = 'clavian.pendingPayment';

/** What a payment is for, as the portal remembers it across the provider's page. */
export interface PendingPayment {
  readonly id: string;
  readonly purpose: 'pass' | 'wallet_topup';
  /** The pass's name, for a pass. */
  readonly name?: string;
  readonly venueSlug?: string | null;
}

export function rememberPendingPayment(pending: PendingPayment): void {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(pending));
  } catch {
    // Storage may be blocked: the return page then says thank you without the details.
  }
}

export function readPendingPayment(): PendingPayment | null {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (raw === null) return null;
    const value = JSON.parse(raw) as Partial<PendingPayment> | null;
    if (value === null || typeof value !== 'object' || typeof value.id !== 'string' || value.id === '') return null;
    if (value.purpose !== 'pass' && value.purpose !== 'wallet_topup') return null;
    return {
      id: value.id,
      purpose: value.purpose,
      ...(typeof value.name === 'string' ? { name: value.name } : {}),
      ...(typeof value.venueSlug === 'string' ? { venueSlug: value.venueSlug } : {}),
    };
  } catch {
    return null;
  }
}

export function clearPendingPayment(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Nothing to clear.
  }
}

/** Remember the payment, then hand the browser to the provider's page. */
export function goToCheckout(pending: PendingPayment, checkoutUrl: string): void {
  rememberPendingPayment(pending);
  window.location.assign(checkoutUrl);
}

/** The extra a café adds to a top-up of `amount`: the largest bonus among the offers the amount reaches, or nothing. */
export function bestTopUpBonus(amount: number, offers: readonly MyTopUpOffer[]): number {
  let best = 0;
  for (const offer of offers) {
    if (amount >= offer.minTopup.amount && offer.bonus.amount > best) best = offer.bonus.amount;
  }
  return best;
}

/** How many minor units make one whole unit of a currency (100 for rupees). */
export function minorPerWhole(currency: string): number {
  try {
    const digits = new Intl.NumberFormat('en', { style: 'currency', currency }).resolvedOptions().maximumFractionDigits ?? 2;
    return 10 ** digits;
  } catch {
    return 100;
  }
}

export type AmountCheck =
  | { readonly ok: true; readonly money: NonNegativeMoney }
  | { readonly ok: false; readonly reason: 'empty' | 'notWhole' | 'tooLow' | 'tooHigh' };

/** An amount the player typed, in whole currency units, against the café's limits. */
export function checkTypedAmount(text: string, min: NonNegativeMoney, max: NonNegativeMoney): AmountCheck {
  const trimmed = text.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };
  if (!/^\d{1,9}$/.test(trimmed)) return { ok: false, reason: 'notWhole' };
  const amount = Number(trimmed) * minorPerWhole(min.currency);
  if (amount < min.amount) return { ok: false, reason: 'tooLow' };
  if (amount > max.amount) return { ok: false, reason: 'tooHigh' };
  return { ok: true, money: { amount, currency: min.currency } };
}
